import React, { useEffect, useReducer, useRef } from 'react';

import { MIN_ZOOM, viewReducer } from './viewReducer';

type UseImageViewerReturn = {
    zoom: number;
    panX: number;
    panY: number;
    containerRef: React.RefObject<HTMLDivElement>;
    handleMouseDown: (e: React.MouseEvent) => void;
    handleMouseMove: (e: React.MouseEvent) => void;
    stopDrag: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
};

export const useImageViewer = (src: string): UseImageViewerReturn => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [{ zoom, panX, panY }, dispatch] = useReducer(viewReducer, {
        zoom: 1,
        panX: 0,
        panY: 0,
    });
    const isDragging = useRef(false);
    const lastMouse = useRef({ x: 0, y: 0 });

    // Reset to fit-view when the displayed image changes.
    useEffect(() => {
        dispatch({ type: 'reset' });
    }, [src]);

    // Wheel zoom — non-passive so we can preventDefault and stop the page scroll.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            dispatch({
                type: 'wheelZoom',
                deltaY: e.deltaY,
                // cursor position relative to container centre
                cursorX: e.clientX - rect.left - rect.width / 2,
                cursorY: e.clientY - rect.top - rect.height / 2,
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom <= MIN_ZOOM) return;
        isDragging.current = true;
        lastMouse.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging.current) return;
        dispatch({
            type: 'pan',
            dx: e.clientX - lastMouse.current.x,
            dy: e.clientY - lastMouse.current.y,
        });
        lastMouse.current = { x: e.clientX, y: e.clientY };
    };

    const stopDrag = () => {
        isDragging.current = false;
    };

    const zoomIn = () => dispatch({ type: 'zoomIn' });
    const zoomOut = () => dispatch({ type: 'zoomOut' });

    return {
        zoom,
        panX,
        panY,
        containerRef,
        handleMouseDown,
        handleMouseMove,
        stopDrag,
        zoomIn,
        zoomOut,
    };
};
